import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  onSnapshot,
  serverTimestamp,
  updateDoc,
} from 'firebase/firestore';
import { auth, db } from '../firebaseConfig';
import '../styles/MyPlaybook.css';
import mapListRaw from '../data/mapList';
import TitleSetter from '../components/TitleSetter';
import TopBar from '../components/TopBar';
import StrategyCard from '../components/MyPlaybook/StrategyCard';
import { fetchActiveSeasonDoc } from '../utils/seasonApi';
import { getSeasonSelection, subscribeSeasonSelection } from '../utils/seasonSelection';
import { exportPlaybookPDF } from '../utils/exportPlaybookPDF';
import { isPlayerRole, normalizeTeamRole } from '../utils/teamRole';

const MODE_OPTIONS = [
  { value: 'ALL', label: 'All' },
  { value: 'HARDPOINT', label: 'HP' },
  { value: 'SND', label: 'SND' },
  { value: 'CONTROL', label: 'CTL' },
];

const MyPlaybook = () => {
  const [strategies, setStrategies] = useState([]);
  const [userProfile, setUserProfile] = useState(null);
  const [teamLogo, setTeamLogo] = useState('');
  const [teamId, setTeamId] = useState(null);
  const [userRole, setUserRole] = useState('player');
  const [activeSeason, setActiveSeason] = useState(null);
  const [modeFilter, setModeFilter] = useState('ALL');
  const [mapFilter, setMapFilter] = useState('');
  const [phaseFilter, setPhaseFilter] = useState('ALL');
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('recent');
  const [showExportPanel, setShowExportPanel] = useState(false);
  const [exportMap, setExportMap] = useState('');
  const [exportPhase, setExportPhase] = useState('Attack');
  const [exportLayout, setExportLayout] = useState('thumbnails');
  const [skipEmptySteps, setSkipEmptySteps] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [selectedStrategyIds, setSelectedStrategyIds] = useState([]);
  const navigate = useNavigate();

  const strategyBasePath = useCallback(() =>
    activeSeason
      ? `teams/${teamId}/seasons/${activeSeason}/strategies`
      : `teams/${teamId}/strategies`, [activeSeason, teamId]);

  const getUpdatedAtMillis = (value) => {
    if (!value) return 0;
    if (value.toMillis) return value.toMillis();
    if (value.toDate) return value.toDate().getTime();
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime();
  };

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (!user) {
        setTeamId(null);
        setStrategies([]);
        return;
      }

      try {
        const userDoc = await getDoc(doc(db, `users/${user.uid}`));
        if (userDoc.exists()) {
          const data = userDoc.data() || {};
          setTeamId(data.teamId || null);
          setUserProfile(data);
          setUserRole(normalizeTeamRole(data.role));
        }
      } catch (error) {
        console.error('Failed to load user profile', error);
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!teamId) return undefined;

    let isMounted = true;
    const storedSeason = getSeasonSelection(teamId);

    if (storedSeason) {
      setActiveSeason(String(storedSeason));
    } else {
      fetchActiveSeasonDoc(teamId)
        .then(({ seasonId }) => {
          if (isMounted) {
            setActiveSeason(seasonId ? String(seasonId) : null);
          }
        })
        .catch((err) => {
          console.error('Failed to load active season for playbook', err);
        });
    }

    const unsubscribe = subscribeSeasonSelection(teamId, (seasonId) => {
      if (isMounted) {
        setActiveSeason(seasonId ? String(seasonId) : null);
      }
    });

    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, [teamId]);

  useEffect(() => {
    if (!teamId) return undefined;

    const stratRef = collection(db, strategyBasePath());
    const unsubscribe = onSnapshot(
      stratRef,
      (snapshot) => {
        const allStrats = snapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));
        const sorted = allStrats.sort((a, b) => {
          const aTime = getUpdatedAtMillis(a.updatedAt) || getUpdatedAtMillis(a.createdAt);
          const bTime = getUpdatedAtMillis(b.updatedAt) || getUpdatedAtMillis(b.createdAt);
          return bTime - aTime;
        });
        setStrategies(sorted);
      },
      (error) => {
        console.error('Failed to load strategies', error);
      }
    );

    return () => unsubscribe();
  }, [teamId, activeSeason, strategyBasePath]);

  useEffect(() => {
    setSelectedStrategyIds((prev) => {
      const availableIds = new Set(strategies.map((strat) => strat.id));
      return prev.filter((id) => availableIds.has(id));
    });
  }, [strategies]);

  useEffect(() => {
    if (exportMap) return;
    const firstMap = strategies.find((s) => s.map)?.map;
    if (firstMap) setExportMap(firstMap);
  }, [strategies, exportMap]);

  const getMapImage = (mapName, mode = 'hardpoint') => {
    const lowerMode = mode.toLowerCase();
    const lowerMap = mapName?.toLowerCase();
    const maps = mapListRaw[lowerMode] || [];
    const match = maps.find((m) => m.name.toLowerCase() === lowerMap);
    return match?.image || '/assets/placeholder.jpg';
  };

  const formatUpdatedAt = (value) => {
    if (!value) return 'N/A';
    if (value.toDate) return value.toDate().toLocaleDateString();
    if (value instanceof Date) return value.toLocaleDateString();
    if (typeof value === 'string' || typeof value === 'number') {
      const parsed = new Date(value);
      return Number.isNaN(parsed.getTime()) ? 'N/A' : parsed.toLocaleDateString();
    }
    return 'N/A';
  };

  const favoriteCount = strategies.filter((strat) => strat.isFavorite).length;

  const mapOptions = [
    ...new Set(
      strategies
        .filter((s) => modeFilter === 'ALL' || s.mode?.toUpperCase() === modeFilter)
        .map((s) => s.map)
        .filter(Boolean)
    ),
  ];

  const getStrategySearchText = (strat) => [
    strat.name,
    strat.map,
    strat.mode,
    ...(strat.tags?.zones || []),
    ...(strat.tags?.phases || []),
    ...(strat.tags?.styles || []),
  ].filter(Boolean).join(' ').toLowerCase();

  const filteredStrategies = strategies
    .filter((strat) => {
      const matchesMode = modeFilter === 'ALL' || strat.mode?.toUpperCase() === modeFilter;
      const matchesMap = !mapFilter || strat.map === mapFilter;
      const matchesPhase = phaseFilter === 'ALL' || strat.tags?.phases?.includes(phaseFilter);
      const matchesFavorite = !showFavoritesOnly || strat.isFavorite;
      const normalizedSearch = searchQuery.trim().toLowerCase();
      const matchesSearch = !normalizedSearch || getStrategySearchText(strat).includes(normalizedSearch);

      return matchesMode && matchesMap && matchesPhase && matchesFavorite && matchesSearch;
    })
    .sort((a, b) => {
      if (sortBy === 'name') {
        return String(a.name || '').localeCompare(String(b.name || ''));
      }

      if (sortBy === 'map') {
        return String(a.map || '').localeCompare(String(b.map || ''));
      }

      if (sortBy === 'favorites') {
        return Number(Boolean(b.isFavorite)) - Number(Boolean(a.isFavorite));
      }

      const aTime = getUpdatedAtMillis(a.updatedAt) || getUpdatedAtMillis(a.createdAt);
      const bTime = getUpdatedAtMillis(b.updatedAt) || getUpdatedAtMillis(b.createdAt);
      return bTime - aTime;
    });

  const toggleSelectedStrategy = (strategyId) => {
    setSelectedStrategyIds((prev) =>
      prev.includes(strategyId)
        ? prev.filter((id) => id !== strategyId)
        : [...prev, strategyId]
    );
  };

  const handleExportPdf = async (selectedOnly = false) => {
    const selectedCount = selectedStrategyIds.length;
    if (!canExportPdf || !teamId || isExporting) return;
    if (selectedOnly && selectedCount === 0) return;
    if (!selectedOnly && (!exportMap || !exportPhase)) return;

    setIsExporting(true);
    try {
      const bytes = await exportPlaybookPDF({
        teamId,
        seasonId: activeSeason,
        mapName: exportMap,
        phase: exportPhase,
        skipEmptySteps,
        layout: exportLayout,
        strategyIds: selectedOnly ? selectedStrategyIds : [],
      });
      const blob = new Blob([bytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = selectedOnly
        ? `Selected-Plays-${selectedCount}.pdf`
        : `${String(exportMap).replace(/\s+/g, '_')}-${exportPhase}-Playbook.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export playbook PDF', error);
      window.alert(error?.message || 'Failed to export PDF.');
    } finally {
      setIsExporting(false);
    }
  };

  const handleToggleFavorite = async (e, strat) => {
    e.stopPropagation();
    if (!teamId) return;
    const stratRef = doc(db, `${strategyBasePath()}/${strat.id}`);
    try {
      await updateDoc(stratRef, {
        isFavorite: !strat.isFavorite,
        updatedAt: serverTimestamp(),
      });
      setStrategies((prev) =>
        prev.map((s) =>
          s.id === strat.id ? { ...s, isFavorite: !s.isFavorite } : s
        )
      );
    } catch (error) {
      console.error('Failed to update favorite', error);
      window.alert('Missing or insufficient permissions to update this strategy.');
    }
  };

  const handleDeleteStrategy = async (e, strat) => {
    e.stopPropagation();
    if (!teamId) return;
    const shouldDelete = window.confirm(
      `Delete "${strat.name || 'this strategy'}"? This cannot be undone.`
    );
    if (!shouldDelete) return;
    const stratRef = doc(db, `${strategyBasePath()}/${strat.id}`);
    try {
      await deleteDoc(stratRef);
    } catch (error) {
      console.error('Failed to delete strategy', error);
      window.alert('Missing or insufficient permissions to delete this strategy.');
    }
  };

  const canDeleteStrategies = !isPlayerRole(userRole);
  const canExportPdf =
    Boolean(userProfile?.isOwner) ||
    ['coach', 'assistant_coach', 'analyst', 'owner'].includes(userRole);

  return (
    <>
      <TitleSetter title="MyPlaybook" />
      <div className="playbook-page">
        <TopBar
          userProfile={userProfile}
          teamLogo={teamLogo}
          setUserProfile={setUserProfile}
          setTeamLogo={setTeamLogo}
        />

        <main className="playbook-container">
          <header className="playbook-hero">
            <div>
              <p className="playbook-eyebrow">Strategy Library</p>
              <h1>My Playbook</h1>
              <p className="playbook-summary">
                {strategies.length} strategies · {favoriteCount} favorites
              </p>
            </div>
          </header>

          <div className="playbook-command-row">
            <label className="playbook-search">
              <span className="sr-only">Search plays</span>
              <input
                type="search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search plays..."
              />
            </label>
            <div className="playbook-primary-actions">
              <button type="button" className="playbook-new-btn" onClick={() => navigate('/my-strategy')}>
                + New Strategy
              </button>
              {canExportPdf && (
                <button
                  type="button"
                  className={`playbook-export-btn ${showExportPanel ? 'is-active' : ''}`}
                  onClick={() => setShowExportPanel((prev) => !prev)}
                >
                  Export
                </button>
              )}
            </div>
          </div>

          {canExportPdf && showExportPanel && (
            <div className="playbook-export-panel">
              <span className="export-selection-count">
                {selectedStrategyIds.length} selected
              </span>
              <select value={exportMap} onChange={(e) => setExportMap(e.target.value)}>
                <option value="">Select map</option>
                {[...new Set(strategies.map((s) => s.map).filter(Boolean))].map((map) => (
                  <option key={map} value={map}>{map}</option>
                ))}
              </select>
              {['Attack', 'Defense'].map((phase) => (
                <button
                  key={phase}
                  type="button"
                  onClick={() => setExportPhase(phase)}
                  className={exportPhase === phase ? 'filter-active' : ''}
                >
                  {phase}
                </button>
              ))}
              <span className="playbook-inline-label">Layout</span>
              {[
                { key: 'thumbnails', label: 'Thumbnails' },
                { key: 'fullWidth', label: 'Full Width' },
              ].map((item) => (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => setExportLayout(item.key)}
                  className={exportLayout === item.key ? 'filter-active' : ''}
                >
                  {item.label}
                </button>
              ))}
              <label className="playbook-checkbox-label">
                <input
                  type="checkbox"
                  checked={skipEmptySteps}
                  onChange={(e) => setSkipEmptySteps(e.target.checked)}
                />
                Skip empty steps
              </label>
              <button type="button" onClick={() => handleExportPdf(false)} disabled={!exportMap || isExporting}>
                {isExporting ? 'Exporting...' : 'Export Filtered PDF'}
              </button>
              <button
                type="button"
                onClick={() => handleExportPdf(true)}
                disabled={!selectedStrategyIds.length || isExporting}
              >
                {isExporting ? 'Exporting...' : `Export Selected PDF (${selectedStrategyIds.length})`}
              </button>
              <button
                type="button"
                onClick={() => setSelectedStrategyIds([])}
                disabled={!selectedStrategyIds.length || isExporting}
              >
                Clear Selected
              </button>
            </div>
          )}

          <section className="playbook-filter-shell" aria-label="Playbook filters">
            <div className="playbook-mode-tabs">
              {MODE_OPTIONS.map((mode) => (
                <button
                  key={mode.value}
                  type="button"
                  onClick={() => {
                    setModeFilter(mode.value);
                    setMapFilter('');
                    setPhaseFilter('ALL');
                  }}
                  className={modeFilter === mode.value ? 'filter-active' : ''}
                >
                  {mode.label}
                </button>
              ))}
              <button
                type="button"
                onClick={() => setShowFavoritesOnly((prev) => !prev)}
                className={showFavoritesOnly ? 'filter-active' : ''}
              >
                {showFavoritesOnly ? 'Favorites' : 'All Plays'}
              </button>
            </div>

            <div className="playbook-select-filters">
              <label>
                <span>Map</span>
                <select value={mapFilter} onChange={(e) => setMapFilter(e.target.value)}>
                  <option value="">All Maps</option>
                  {mapOptions.map((map) => (
                    <option key={map} value={map}>{map}</option>
                  ))}
                </select>
              </label>

              <label>
                <span>Phase</span>
                <select value={phaseFilter} onChange={(e) => setPhaseFilter(e.target.value)}>
                  <option value="ALL">All</option>
                  <option value="Attack">Attack</option>
                  <option value="Defense">Defense</option>
                </select>
              </label>

              <label>
                <span>Sort</span>
                <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                  <option value="recent">Recently Updated</option>
                  <option value="name">Name</option>
                  <option value="map">Map</option>
                  <option value="favorites">Favorites First</option>
                </select>
              </label>
            </div>
          </section>

          <div className="playbook-divider" />

          <div className="playbook-grid">
            {filteredStrategies.map((strat) => {
              const imageSrc = getMapImage(strat.map, strat.mode);
              const favoriteLabel = strat.isFavorite ? '★' : '☆';

              return (
                <StrategyCard
                  key={strat.id}
                  strat={strat}
                  imageSrc={imageSrc}
                  favoriteLabel={favoriteLabel}
                  lastUpdated={formatUpdatedAt(strat.updatedAt)}
                  isSelected={selectedStrategyIds.includes(strat.id)}
                  onOpen={(item) => navigate(`/strategy/${item.id}`)}
                  onToggleSelected={toggleSelectedStrategy}
                  onToggleFavorite={handleToggleFavorite}
                  onDelete={canDeleteStrategies ? handleDeleteStrategy : undefined}
                />
              );
            })}

            {filteredStrategies.length === 0 && (
              <div className="playbook-empty-state">
                <h3>No Plays Found</h3>
                <p>Try adjusting your search, filters, or favorites view.</p>
              </div>
            )}
          </div>
        </main>
      </div>
    </>
  );
};

export default MyPlaybook;
